import * as React from "react";
import Box from "@mui/material/Box";
import { useQuery } from "react-query";
import {
  AssetApi,
  AssetTransactionDto,
  ETransactionType,
} from "../../../services/netServices";
import { useEffect } from "react";
import DataGridCustom from "../../../common/components/DataGrid";
import {
  GridColDef,
  gridPageCountSelector,
  GridPagination,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TablePaginationProps,
  TextField,
  Tooltip,
} from "@mui/material";
import TransactionModal from "./TransactionModal";
import { useStores } from "../../../models/helpers/useStores";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import MuiPagination from "@mui/material/Pagination";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TransactionDetailModal from "./TransactionDetailModal";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import ImportToUpdateModal from "./ImportToUpdateModal";

export default function CollumTransaction() {
  const { t } = useTranslation() as any;
  const {
    appStore: { userProfile, getACLGroup },
  } = useStores();
  const [selectedType, setSelectedType] = React.useState<ETransactionType>(
    ETransactionType.NUMBER_0
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    React.useState(searchTerm);
  const [previewImages, setPreviewImages] = React.useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = React.useState<any[]>([]);
  const [openImportToUpdateModal, setOpenImportToUpdateModal] = React.useState(false);

  const handleOpenPreview = (images: string[], index: number) => {
    setPreviewImages(images);
    setCurrentIndex(index);
  };
  const [transactionDetailModal, setTransactionDetailModal] = React.useState<{
    visible: boolean;
    data: AssetTransactionDto | null;
  }>({
    visible: false,
    data: null,
  });
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % previewImages.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + previewImages.length) % previewImages.length
    );
  };
  const handleClose = () => setOpen(false);
  const handleCloseImage = () => {
    setPreviewImages([]);
    setCurrentIndex(0);
  };
  useEffect(() => {
    if (previewImages.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") handleCloseImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewImages, handleNext, handlePrev, handleCloseImage]);
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);
  const { data, refetch, isFetching } = useQuery(
    [
      "/Asset/Transactions",
      paginationModel,
      debouncedSearchTerm,
      selectedType,
      sortModel,
    ],
    async () => {
      const sortField = sortModel[0]?.field ?? null;
      const sortDirection = sortModel[0]?.sort ?? null;

      const isDescending = sortDirection === "desc";

      const { data } = await new AssetApi().assetTransactionsGet(
        paginationModel.page + 1,
        paginationModel.pageSize,
        debouncedSearchTerm,
        selectedType,
        sortField,
        sortDirection ? isDescending : undefined
      );
      return data;
    },
    { keepPreviousData: true }
  );

  useEffect(() => {
    refetch();
  }, [selectedType, refetch, debouncedSearchTerm, paginationModel, sortModel]);

  const transformedData =
    data?.Data?.map((item: any, index: number) => ({
      ...item,
      id: index,
      stt: index + 1,
    })) ?? [];
  const { aclAsset } = React.useMemo(() => {
    const aclGroup = getACLGroup()?.[userProfile?.PrimaryGroup?.GroupId ?? 0];
    return {
      aclAsset: aclGroup?.asset,
    };
  }, [userProfile]);
  const getDisplayValue = (value: any) => {
    return value === null || value === undefined || value === "" ? "-" : value;
  };
  const columns: Array<GridColDef<AssetTransactionDto>> = [
    { field: "stt", headerName: "STT", sortable: false, width: 50 },
    {
      field: "SerialNumber",
      headerName: t("34_00_serial_number"),
      flex: 1,
      sortable: true,
      renderCell: (params) => getDisplayValue(params.value),
    },
    {
      field: "AssetName",
      headerName: t("34_00_asset_name"),
      flex: 1,
      sortable: true,
      renderCell: (params) => getDisplayValue(params.value),
    },
    {
      field: "TransactionType",
      headerName: t("34_00_transaction_type"),
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        switch (params.value) {
          case "add":
            return t("34_00_add");
          case "assign":
            return t("34_00_assign");
          case "retire":
            return t("34_00_retire");
          case "transfer":
            return t("34_00_transfer");
          case "restock":
            return t("34_00_restock");
          case "sell":
            return t("34_00_sell");
          default:
            return getDisplayValue(params.value);
        }
      },
    },
    {
      field: "Quantity",
      headerName: t("00_06_quantity"),
      flex: 1,
      sortable: false,
      renderCell: (params) => getDisplayValue(params.value),
    },
    {
      field: "TransactionDate",
      headerName: t("34_00_transaction_date"),
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const formattedDate = params.value
          ? dayjs(params.value * 1000).format("DD/MM/YYYY")
          : "-";

        return (
          <Tooltip title={formattedDate}>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "Updated",
      headerName: t("34_00_updated_date"),
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const formattedDate = params.value
          ? dayjs(params.value * 1000).format("DD/MM/YYYY HH:mm:ss")
          : "-";

        return (
          <Tooltip title={formattedDate}>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "TransactionImages",
      headerName: t("34_00_image"),
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const images = params.value;
        if (!Array.isArray(images) || images.length === 0) {
          return "-";
        }
        if (images.length >= 1) {
          return (
            <Box>
              <CropOriginalIcon
                sx={{ fontSize: "16px", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPreview(images, 0);
                }}
              />
            </Box>
          );
        }
      },
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function Pagination({
    page,
    onPageChange,
    className,
  }: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <MuiPagination
        color="primary"
        className={className}
        count={pageCount}
        page={page + 1}
        onChange={(event, newPage) => {
          onPageChange(event as any, newPage - 1);
        }}
        showFirstButton
        showLastButton
      />
    );
  }
  function CustomPagination(props: any): JSX.Element {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
  }
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 2,
        }}
      >
        <Box
          sx={{ display: "flex", gap: 4, alignItems: "center", width: "50%" }}
        >
          <FormControl fullWidth sx={{ maxWidth: 120, height: 40 }}>
            <Select
              value={selectedType}
              onChange={(e) =>
                setSelectedType(e.target.value as ETransactionType)
              }
              sx={{ height: 40, padding: 0 }}
              inputProps={{
                sx: {
                  height: 40,
                  padding: "0 14px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <MenuItem value={ETransactionType.NUMBER_0}>
                {t("34_00_all")}
              </MenuItem>
              <MenuItem value={ETransactionType.NUMBER_1}>
                {t("34_00_add")}
              </MenuItem>
              <MenuItem value={ETransactionType.NUMBER_2}>
                {t("34_00_assign")}
              </MenuItem>
              <MenuItem value={ETransactionType.NUMBER_3}>
                {t("34_00_transfer")}
              </MenuItem>
              <MenuItem value={ETransactionType.NUMBER_4}>
                {t("34_00_retire")}
              </MenuItem>
              <MenuItem value={ETransactionType.NUMBER_5}>
                {t("34_00_sell")}
              </MenuItem>
              <MenuItem value={ETransactionType.NUMBER_6}>
                {t("34_00_restock")}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            placeholder={t("00_06_search")}
            size="small"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
            autoComplete="off"
          />
        </Box>
        <Box sx={{ display: "flex", gap: 4 }}>
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 400,
              color: "rgb(99, 99, 99)",
            }}
            onClick={() => setOpenImportToUpdateModal(true)}
          >
            {t("00_03_import")}
          </Button>
          {aclAsset?.AddTransaction && (
          <Button
            variant="contained"
            style={{ color: "#fff", backgroundColor: "#08B0A0" }}
            onClick={handleOpen}
          >
            + {t("34_00_new_transaction")}
          </Button>
        )}
        </Box>
       
        {open && (
          <TransactionModal
            open={open}
            onClose={handleClose}
            refetchList={refetch}
            selectedType={selectedType}
          />
        )}
      </Box>
      <Box>
        <DataGridCustom
          loading={isFetching}
          rows={transformedData ?? []}
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={data?.Totals ?? 0}
          paginationMode="server"
          sortingMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          sortingOrder={["asc", "desc"]}
          pageSizeOptions={[5, 10, 20, 50]}
          slots={{ pagination: CustomPagination }}
          localeText={{ noRowsLabel: t("00_06_no_data") }}
          slotProps={{ pagination: { labelRowsPerPage: "" } }}
          onCellClick={(params, event) => {
            setTransactionDetailModal({
              visible: true,
              data: params.row,
            });
          }}
        />
      </Box>
      <Modal open={previewImages.length > 0} onClose={handleCloseImage}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.8)",
          }}
          onClick={handleCloseImage}
        >
          {previewImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              sx={{ position: "absolute", left: 20, color: "white" }}
            >
              <ArrowBackIosNewIcon fontSize="large" />
            </IconButton>
          )}

          <img
            src={previewImages[currentIndex]}
            alt={`preview-${currentIndex}`}
            style={{
              maxHeight: "auto",
              maxWidth: "500px",
              objectFit: "contain",
              borderRadius: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {previewImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              sx={{ position: "absolute", right: 20, color: "white" }}
            >
              <ArrowForwardIosIcon fontSize="large" />
            </IconButton>
          )}
        </Box>
      </Modal>
      {transactionDetailModal.visible && (
        <TransactionDetailModal
          open={transactionDetailModal.visible}
          onClose={() =>
            setTransactionDetailModal({ visible: false, data: null })
          }
          data={transactionDetailModal.data}
        />
      )}
      {openImportToUpdateModal && (
        <ImportToUpdateModal
          open={openImportToUpdateModal}
          onClose={() => setOpenImportToUpdateModal(false)}
          onSuccess={() => {
            refetch();
            setOpenImportToUpdateModal(false);
          }}
        />
      )}
      <ToastContainer
        position="bottom-left"
        autoClose={3000}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="custom-toast"
        hideProgressBar={true}
        icon={false}
      />
    </Box>
  );
}
